import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { useAuthStore } from '@/app/stores/auth'
import { EMPLOYER_SOFTWARES, SOFTWARE_IDS, type SoftwareCode } from '@/app/stores/session'

/** A route shared with other softwares (`meta.sharedWith`) and the softwares the user has (`session.softwares`). */
export interface SharedRouteAccess {
  sharedWith?: readonly SoftwareCode[]
  userSoftwares: readonly string[]
}

/**
 * The software a screen works in (wp/shell §1.3): the first URL segment when it is one of the five employer softwares
 * (`re`, `on`, `hr`, `en`, `ac`), else the session's stored software — so `ca` / `em` always come from the session,
 * never from the URL (candidate-app, dashboards). On a route shared with other softwares (`meta.sharedWith`), a user who
 * lacks the URL's software works in the shared one they have — the stored software when it is one of them: an employee
 * on `/en/surveys/:id` is in `em` (engagement OPEN-1 (a), SHARED_CHANGES_QUEUE #17).
 */
export function resolveSoftware(
  path: string,
  stored: SoftwareCode | null,
  shared?: SharedRouteAccess,
): SoftwareCode | null {
  const segment = path.split('/')[1] ?? ''
  if (!(EMPLOYER_SOFTWARES as readonly string[]).includes(segment)) return stored
  if (shared && !shared.userSoftwares.includes(segment)) {
    const own = (shared.sharedWith ?? []).filter((code) => shared.userSoftwares.includes(code))
    if (own.length) return stored && own.includes(stored) ? stored : (own[0] ?? null)
  }
  return segment as SoftwareCode
}

export function useCurrentSoftware(): {
  software: ComputedRef<SoftwareCode | null>
  softwareId: ComputedRef<number | null>
} {
  const route = useRoute()
  const auth = useAuthStore()
  const software = computed(() =>
    resolveSoftware(route.path, auth.currentSoftware, {
      sharedWith: route.meta.sharedWith,
      userSoftwares: auth.session?.softwares.map((entry) => entry.short) ?? [],
    }),
  )
  const softwareId = computed(() => (software.value ? SOFTWARE_IDS[software.value] : null))
  return { software, softwareId }
}
